import React, { useState, useEffect, useRef, useCallback } from 'react';
import QRCode from 'qrcode';
import jsQR from 'jsqr';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Download, Upload, Camera, QrCode, Palette, Image as ImageIcon, 
  Shield, Info, Sparkles, Check, Lock, Zap, Type, CheckCircle, X
} from 'lucide-react';

// ========== CRYPTO FUNCTIONS (INLINE) ==========
const encryptForEmbed = (data, password) => {
  if (!password) return data;
  
  try {
    const encoder = new TextEncoder();
    const dataBytes = encoder.encode(data);
    const passwordBytes = encoder.encode(password);
    
    // Simple XOR encryption for demo (use real crypto in production)
    const encrypted = new Uint8Array(dataBytes.length);
    for (let i = 0; i < dataBytes.length; i++) {
      encrypted[i] = dataBytes[i] ^ passwordBytes[i % passwordBytes.length];
    }
    
    // Prepend marker to indicate encryption
    const marker = 'ENC:';
    const encryptedB64 = btoa(String.fromCharCode(...encrypted));
    return marker + encryptedB64;
  } catch (error) {
    console.error('Encryption error:', error);
    return data;
  }
};

const decryptFromEmbed = (data, password) => {
  if (!data.startsWith('ENC:')) return data;
  
  if (!password) {
    throw new Error('password required');
  }
  
  try {
    const encryptedB64 = data.substring(4);
    const encryptedStr = atob(encryptedB64);
    const encrypted = new Uint8Array(encryptedStr.split('').map(c => c.charCodeAt(0)));
    
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();
    const passwordBytes = encoder.encode(password);
    
    const decrypted = new Uint8Array(encrypted.length);
    for (let i = 0; i < encrypted.length; i++) {
      decrypted[i] = encrypted[i] ^ passwordBytes[i % passwordBytes.length];
    }
    
    return decoder.decode(decrypted);
  } catch (error) {
    throw new Error('Decryption failed - wrong password');
  }
};

// ========== MAIN COMPONENT ==========
const QRCodeStudio = () => {
  // Generator State
  const [qrText, setQrText] = useState('https://example.com');
  const [qrPassword, setQrPassword] = useState('');
  const [errorCorrectionLevel, setErrorCorrectionLevel] = useState('M');
  const [qrSize, setQrSize] = useState([300]);
  const [qrColor, setQrColor] = useState('#04D9FF');
  const [qrBgColor, setQrBgColor] = useState('#0B1020');
  const [margin, setMargin] = useState([4]);
  const [qrCanvas, setQrCanvas] = useState(null);
  
  // Logo State
  const [logoImage, setLogoImage] = useState(null);
  const [logoSize, setLogoSize] = useState([22]);
  const [logoShape, setLogoShape] = useState('circle');
  const [logoBorder, setLogoBorder] = useState(true);
  
  // Text Logo Creator State
  const [textLogoText, setTextLogoText] = useState('QR');
  const [textLogoFont, setTextLogoFont] = useState('Arial');
  const [textLogoColor, setTextLogoColor] = useState('#FFFFFF');
  const [textLogoBgColor, setTextLogoBgColor] = useState('#04D9FF');
  const [textLogoFontSize, setTextLogoFontSize] = useState([48]);
  const [textLogoPreviewUrl, setTextLogoPreviewUrl] = useState(null);
  
  // Frame State
  const [frameEnabled, setFrameEnabled] = useState(false);
  const [frameStyle, setFrameStyle] = useState('rounded');
  const [frameText, setFrameText] = useState('Scan Me');
  
  // Scanner State
  const [scanResult, setScanResult] = useState('');
  const [scanPassword, setScanPassword] = useState('');
  const [scanning, setScanning] = useState(false);
  const [rawScannedData, setRawScannedData] = useState('');
  const videoRef = useRef(null);
  const scanIntervalRef = useRef(null);
  
  const canvasRef = useRef(null);
  const logoInputRef = useRef(null);
  const scanFileInputRef = useRef(null);
  const textLogoCanvasRef = useRef(null);

  // Color Palettes
  const colorPalettes = [
    { name: 'Neon Cyan', fg: '#04D9FF', bg: '#0B1020' },
    { name: 'Neon Green', fg: '#39FF14', bg: '#050814' },
    { name: 'Neon Purple', fg: '#BC13FE', bg: '#12071F' },
    { name: 'Neon Pink', fg: '#F72798', bg: '#1A0510' },
    { name: 'Classic', fg: '#000000', bg: '#FFFFFF' },
    { name: 'Gradient Blue', fg: '#00FFC6', bg: '#020716' },
  ];

  // Font options
  const fontOptions = [
    'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Courier New',
    'Verdana', 'Trebuchet MS', 'Impact', 'Comic Sans MS', 'Monospace'
  ];

  const roundRect = (ctx, x, y, width, height, radius) => {
    if (width < 2 * radius) radius = width / 2;
    if (height < 2 * radius) radius = height / 2;
    
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };

  // Draw logo function
  const drawLogo = useCallback((ctx, logoImg, qrSizeVal, offsetX, offsetY) => {
    const logoSizePixels = (qrSizeVal * logoSize[0]) / 100;
    const logoX = offsetX + (qrSizeVal - logoSizePixels) / 2;
    const logoY = offsetY + (qrSizeVal - logoSizePixels) / 2;
    
    ctx.save();
    
    // Draw background for logo
    if (logoBorder) {
      ctx.fillStyle = qrBgColor;
      ctx.beginPath();
      if (logoShape === 'circle') {
        ctx.arc(logoX + logoSizePixels / 2, logoY + logoSizePixels / 2, 
                logoSizePixels / 2 + 5, 0, Math.PI * 2);
      } else if (logoShape === 'rounded') {
        roundRect(ctx, logoX - 5, logoY - 5, logoSizePixels + 10, logoSizePixels + 10, 8);
      } else {
        ctx.rect(logoX - 5, logoY - 5, logoSizePixels + 10, logoSizePixels + 10);
      }
      ctx.fill();
    }
    
    // Clip logo shape
    ctx.beginPath();
    if (logoShape === 'circle') {
      ctx.arc(logoX + logoSizePixels / 2, logoY + logoSizePixels / 2, 
              logoSizePixels / 2, 0, Math.PI * 2);
    } else if (logoShape === 'rounded') {
      roundRect(ctx, logoX, logoY, logoSizePixels, logoSizePixels, 5);
    } else {
      ctx.rect(logoX, logoY, logoSizePixels, logoSizePixels);
    }
    ctx.clip();
    
    // Draw logo
    ctx.drawImage(logoImg, logoX, logoY, logoSizePixels, logoSizePixels);
    
    ctx.restore();
  }, [logoSize, logoShape, logoBorder, qrBgColor]);

  // Generate QR Code - FIXED: Removed pattern style modifications that corrupt QR
  const generateQRCode = useCallback(async () => {
    try {
      const size = qrSize[0];
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Encrypt data if password provided
      const dataToEncode = encryptForEmbed(qrText, qrPassword);
      
      // Set canvas size (larger if frame enabled)
      const frameSize = frameEnabled ? 80 : 0;
      canvas.width = size + frameSize;
      canvas.height = size + frameSize;
      
      // Draw frame background
      if (frameEnabled) {
        ctx.fillStyle = qrBgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw frame border
        ctx.strokeStyle = qrColor;
        ctx.lineWidth = 3;
        if (frameStyle === 'rounded') {
          ctx.beginPath();
          roundRect(ctx, 5, 5, canvas.width - 10, canvas.height - 10, 15);
          ctx.stroke();
        } else if (frameStyle === 'sharp') {
          ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);
        } else if (frameStyle === 'double') {
          ctx.strokeRect(3, 3, canvas.width - 6, canvas.height - 6);
          ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);
        }
        
        // Draw frame text
        if (frameText) {
          ctx.fillStyle = qrColor;
          ctx.font = 'bold 16px Inter, system-ui, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(frameText, canvas.width / 2, canvas.height - 20);
        }
      }
      
      // Generate QR on temporary canvas - FIXED: Using standard QR generation
      const tempCanvas = document.createElement('canvas');
      await QRCode.toCanvas(tempCanvas, dataToEncode, {
        width: size,
        margin: margin[0],
        errorCorrectionLevel: errorCorrectionLevel,
        color: {
          dark: qrColor,
          light: qrBgColor
        }
      });
      
      // Draw QR on main canvas without pattern modifications
      const offsetX = frameEnabled ? frameSize / 2 : 0;
      const offsetY = frameEnabled ? frameSize / 2 - (frameText ? 20 : 0) : 0;
      ctx.drawImage(tempCanvas, offsetX, offsetY);
      
      // Draw logo if provided
      if (logoImage) {
        drawLogo(ctx, logoImage, size, offsetX, offsetY);
      }
      
      setQrCanvas(canvas);
      
      // Update the preview canvas
      if (canvasRef.current) {
        const previewCtx = canvasRef.current.getContext('2d');
        canvasRef.current.width = canvas.width;
        canvasRef.current.height = canvas.height;
        previewCtx.drawImage(canvas, 0, 0);
      }
    } catch (error) {
      console.error('QR generation error:', error);
      toast.error('Failed to generate QR code');
    }
  }, [qrText, qrPassword, errorCorrectionLevel, qrSize, qrColor, qrBgColor, margin, logoImage, frameEnabled, frameStyle, frameText, drawLogo]);

  // Generate text logo - FIXED: Now properly updates preview URL
  const generateTextLogo = useCallback(() => {
    const canvas = textLogoCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const size = 200;
    
    canvas.width = size;
    canvas.height = size;
    
    ctx.clearRect(0, 0, size, size);
    
    ctx.fillStyle = textLogoBgColor;
    
    if (logoShape === 'circle') {
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.fill();
    } else if (logoShape === 'rounded') {
      roundRect(ctx, 0, 0, size, size, 30);
      ctx.fill();
    } else {
      ctx.fillRect(0, 0, size, size);
    }
    
    ctx.fillStyle = textLogoColor;
    ctx.font = `bold ${textLogoFontSize[0]}px ${textLogoFont}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    let textToDisplay = textLogoText;
    if (textLogoText.length > 4) {
      textToDisplay = textLogoText.substring(0, 4).toUpperCase();
    }
    
    ctx.fillText(textToDisplay, size / 2, size / 2);
    
    if (logoBorder) {
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 4;
      
      if (logoShape === 'circle') {
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
        ctx.stroke();
      } else if (logoShape === 'rounded') {
        roundRect(ctx, 2, 2, size - 4, size - 4, 28);
        ctx.stroke();
      } else {
        ctx.strokeRect(2, 2, size - 4, size - 4);
      }
    }
    
    // FIXED: Update preview URL after drawing
    setTextLogoPreviewUrl(canvas.toDataURL('image/png'));
  }, [textLogoText, textLogoFont, textLogoColor, textLogoBgColor, textLogoFontSize, logoShape, logoBorder]);

  // Use text logo in QR - FIXED: Properly loads image from canvas
  const useTextLogoInQR = () => {
    const canvas = textLogoCanvasRef.current;
    if (!canvas) {
      toast.error('No text logo to apply');
      return;
    }
    
    const dataUrl = canvas.toDataURL('image/png');
    const img = new Image();
    img.onload = () => {
      setLogoImage(img);
      toast.success('Text logo applied to QR code!');
    };
    img.onerror = () => {
      toast.error('Failed to apply text logo');
    };
    img.src = dataUrl;
  };

  useEffect(() => {
    generateQRCode();
  }, [generateQRCode]);

  useEffect(() => {
    generateTextLogo();
  }, [generateTextLogo]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          setLogoImage(img);
          toast.success('Logo uploaded successfully');
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const downloadQR = () => {
    if (!qrCanvas) return;
    const link = document.createElement('a');
    link.download = `qr-code-${Date.now()}.png`;
    link.href = qrCanvas.toDataURL('image/png', 1.0);
    link.click();
    toast.success('QR code downloaded!');
  };

  // Scanner Functions
  const startCameraScanning = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setScanning(true);
        
        scanIntervalRef.current = setInterval(() => {
          scanFromVideo();
        }, 500);
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Camera access denied or not available');
    }
  };

  const stopScanning = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }
    setScanning(false);
  };

  const scanFromVideo = () => {
    if (!videoRef.current) return;
    
    const canvas = document.createElement('canvas');
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height);
    
    if (code) {
      handleScanResult(code.data);
      stopScanning();
    }
  };

  // FIXED: Improved QR scanning from file
  const handleScanFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          // Create canvas with proper dimensions
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          
          // Draw image with white background first (for transparent PNGs)
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: 'attemptBoth'
          });
          
          if (code) {
            handleScanResult(code.data);
          } else {
            // Try with inverted colors
            const invertedData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            for (let i = 0; i < invertedData.data.length; i += 4) {
              invertedData.data[i] = 255 - invertedData.data[i];
              invertedData.data[i + 1] = 255 - invertedData.data[i + 1];
              invertedData.data[i + 2] = 255 - invertedData.data[i + 2];
            }
            const invertedCode = jsQR(invertedData.data, invertedData.width, invertedData.height);
            
            if (invertedCode) {
              handleScanResult(invertedCode.data);
            } else {
              toast.error('No QR code found in image. Try a clearer image or use Classic (black on white) colors.');
            }
          }
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleScanResult = (data) => {
    setRawScannedData(data);
    try {
      const decrypted = decryptFromEmbed(data, scanPassword);
      setScanResult(decrypted);
      toast.success('QR code scanned successfully!');
    } catch (error) {
      if (error.message.includes('password')) {
        toast.error('This QR code is password protected');
        setScanResult('🔒 Password Protected - Enter password to decrypt');
      } else {
        toast.error('Failed to decode QR code');
      }
    }
  };

  const decryptWithPassword = () => {
    if (rawScannedData) {
      try {
        const decrypted = decryptFromEmbed(rawScannedData, scanPassword);
        setScanResult(decrypted);
        toast.success('Decrypted successfully!');
      } catch (error) {
        toast.error('Wrong password');
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="text-center space-y-4">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
          Advanced QR Studio
        </h2>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Create stunning QR codes with logos, frames, and password protection. All processing happens locally in your browser.
        </p>
      </div>

      <Tabs defaultValue="generate" className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-[800px] mx-auto mb-8 bg-gray-800/50">
          <TabsTrigger value="generate" className="flex items-center gap-2 data-[state=active]:bg-cyan-600" data-testid="tab-generate">
            <QrCode className="w-4 h-4" />
            Generate
          </TabsTrigger>
          <TabsTrigger value="logo" className="flex items-center gap-2 data-[state=active]:bg-cyan-600" data-testid="tab-logo">
            <ImageIcon className="w-4 h-4" />
            Logo Maker
          </TabsTrigger>
          <TabsTrigger value="scan" className="flex items-center gap-2 data-[state=active]:bg-cyan-600" data-testid="tab-scan">
            <Camera className="w-4 h-4" />
            Scan
          </TabsTrigger>
          <TabsTrigger value="help" className="flex items-center gap-2 data-[state=active]:bg-cyan-600" data-testid="tab-help">
            <Shield className="w-4 h-4" />
            How It Works
          </TabsTrigger>
        </TabsList>

        {/* Generate Tab */}
        <TabsContent value="generate" className="space-y-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left: Configuration */}
            <div className="space-y-6">
              <Card className="border-gray-700 bg-gray-800/50 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl text-white">
                    <Zap className="w-5 h-5 text-cyan-400" />
                    QR Content
                  </CardTitle>
                  <CardDescription className="text-gray-400">Enter the data you want to encode in the QR code</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <Label htmlFor="qr-text" className="text-base font-medium text-white">Text or URL</Label>
                    <Textarea
                      id="qr-text"
                      data-testid="qr-text-input"
                      placeholder="https://example.com"
                      value={qrText}
                      onChange={(e) => setQrText(e.target.value)}
                      rows={3}
                      className="min-h-[100px] resize-none bg-gray-900 border-gray-600 text-white"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="qr-password" className="text-base font-medium flex items-center gap-2 text-white">
                      <Lock className="w-4 h-4" />
                      Password Protection (Optional)
                    </Label>
                    <Input
                      id="qr-password"
                      data-testid="qr-password-input"
                      type="password"
                      placeholder="Leave empty for no protection"
                      value={qrPassword}
                      onChange={(e) => setQrPassword(e.target.value)}
                      className="h-11 bg-gray-900 border-gray-600 text-white"
                    />
                    {qrPassword && (
                      <p className="text-sm text-cyan-400 flex items-center gap-2">
                        <Check className="w-3 h-3" />
                        QR code will be encrypted
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <Label className="text-base font-medium text-white">Error Correction</Label>
                      <Select value={errorCorrectionLevel} onValueChange={setErrorCorrectionLevel}>
                        <SelectTrigger className="h-11 bg-gray-900 border-gray-600 text-white">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-600">
                          <SelectItem value="L">Low (7% recovery)</SelectItem>
                          <SelectItem value="M">Medium (15% recovery)</SelectItem>
                          <SelectItem value="Q">Quartile (25% recovery)</SelectItem>
                          <SelectItem value="H">High (30% recovery)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-3">
                      <Label className="text-base font-medium text-white">QR Size</Label>
                      <div className="pt-2">
                        <Slider
                          value={qrSize}
                          onValueChange={setQrSize}
                          min={200}
                          max={600}
                          step={50}
                          className="w-full"
                        />
                        <p className="text-sm text-center text-gray-400 mt-2">{qrSize[0]}px</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-base font-medium text-white">Margin</Label>
                    <div className="pt-2">
                      <Slider
                        value={margin}
                        onValueChange={setMargin}
                        min={0}
                        max={10}
                        step={1}
                        className="w-full"
                      />
                      <p className="text-sm text-center text-gray-400 mt-2">{margin[0]} modules</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Color Customization Card */}
              <Card className="border-gray-700 bg-gray-800/50 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl text-white">
                    <Palette className="w-5 h-5 text-purple-400" />
                    Color Customization
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label className="text-base font-medium mb-3 block text-white">Color Presets</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {colorPalettes.map((palette) => (
                        <button
                          key={palette.name}
                          onClick={() => {
                            setQrColor(palette.fg);
                            setQrBgColor(palette.bg);
                          }}
                          className="group relative overflow-hidden rounded-xl p-4 border-2 border-gray-600 hover:border-cyan-400 transition-all duration-300 hover:scale-105"
                          style={{
                            background: `linear-gradient(135deg, ${palette.bg} 0%, ${palette.fg} 100%)`,
                          }}
                          data-testid={`color-preset-${palette.name.toLowerCase().replace(' ', '-')}`}
                        >
                          <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors" />
                          <span className="relative text-xs font-bold text-white drop-shadow-md">
                            {palette.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <Label htmlFor="qr-color" className="text-base font-medium text-white">Foreground Color</Label>
                      <div className="flex gap-3 items-center">
                        <div 
                          className="w-12 h-12 rounded-lg border-2 border-gray-600 overflow-hidden cursor-pointer"
                          style={{ backgroundColor: qrColor }}
                        >
                          <input
                            id="qr-color"
                            type="color"
                            value={qrColor}
                            onChange={(e) => setQrColor(e.target.value)}
                            className="w-full h-full cursor-pointer opacity-0"
                          />
                        </div>
                        <Input
                          type="text"
                          value={qrColor}
                          onChange={(e) => setQrColor(e.target.value)}
                          className="flex-1 h-11 bg-gray-900 border-gray-600 text-white"
                          placeholder="#000000"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label htmlFor="qr-bg" className="text-base font-medium text-white">Background Color</Label>
                      <div className="flex gap-3 items-center">
                        <div 
                          className="w-12 h-12 rounded-lg border-2 border-gray-600 overflow-hidden cursor-pointer"
                          style={{ backgroundColor: qrBgColor }}
                        >
                          <input
                            id="qr-bg"
                            type="color"
                            value={qrBgColor}
                            onChange={(e) => setQrBgColor(e.target.value)}
                            className="w-full h-full cursor-pointer opacity-0"
                          />
                        </div>
                        <Input
                          type="text"
                          value={qrBgColor}
                          onChange={(e) => setQrBgColor(e.target.value)}
                          className="flex-1 h-11 bg-gray-900 border-gray-600 text-white"
                          placeholder="#FFFFFF"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="frame-enabled" className="text-base font-medium text-white">Frame & Text</Label>
                      <Switch
                        id="frame-enabled"
                        checked={frameEnabled}
                        onCheckedChange={setFrameEnabled}
                      />
                    </div>
                    
                    {frameEnabled && (
                      <div className="space-y-4 pl-2 border-l-2 border-cyan-600/50">
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-white">Frame Style</Label>
                          <Select value={frameStyle} onValueChange={setFrameStyle}>
                            <SelectTrigger className="h-9 bg-gray-900 border-gray-600 text-white">
                              <SelectValue placeholder="Select style" />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-gray-600">
                              <SelectItem value="rounded">Rounded</SelectItem>
                              <SelectItem value="sharp">Sharp</SelectItem>
                              <SelectItem value="double">Double Border</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-3">
                          <Label htmlFor="frame-text" className="text-sm font-medium text-white">Frame Text</Label>
                          <Input
                            id="frame-text"
                            placeholder="Scan Me"
                            value={frameText}
                            onChange={(e) => setFrameText(e.target.value)}
                            className="h-9 bg-gray-900 border-gray-600 text-white"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <Alert className="bg-amber-900/30 border-amber-600/50">
                    <Info className="w-4 h-4 text-amber-400" />
                    <AlertDescription className="text-amber-200">
                      <strong>Tip:</strong> For best scanning compatibility, use the "Classic" (black on white) preset or high contrast colors.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </div>

            {/* Right: Preview */}
            <Card className="border-gray-700 bg-gray-800/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl text-white">
                  <Sparkles className="w-5 h-5 text-pink-400" />
                  Live Preview
                </CardTitle>
                <CardDescription className="text-gray-400">Preview your QR code in real-time</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-8 rounded-2xl border border-gray-700 flex items-center justify-center min-h-[400px]">
                  <div className="relative">
                    <canvas
                      ref={canvasRef}
                      data-testid="qr-preview-canvas"
                      className="max-w-full h-auto rounded-lg shadow-2xl"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '400px'
                      }}
                    />
                    {logoImage && (
                      <div className="absolute top-2 right-2 bg-gray-900/80 backdrop-blur-sm text-xs px-2 py-1 rounded-full flex items-center gap-1 text-white">
                        <ImageIcon className="w-3 h-3" />
                        Logo
                      </div>
                    )}
                    {qrPassword && (
                      <div className="absolute top-2 left-2 bg-gray-900/80 backdrop-blur-sm text-xs px-2 py-1 rounded-full flex items-center gap-1 text-cyan-400">
                        <Lock className="w-3 h-3" />
                        Encrypted
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Button 
                    onClick={downloadQR} 
                    data-testid="download-qr-btn"
                    className="w-full h-12 bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white font-bold text-base"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download QR Code (PNG)
                  </Button>
                  
                  <div className="grid grid-cols-3 gap-2 text-center text-sm text-gray-400">
                    <div className="space-y-1">
                      <div className="font-medium text-white">Size</div>
                      <div>{qrSize[0]}px</div>
                    </div>
                    <div className="space-y-1">
                      <div className="font-medium text-white">Format</div>
                      <div>PNG</div>
                    </div>
                    <div className="space-y-1">
                      <div className="font-medium text-white">Error Level</div>
                      <div>{errorCorrectionLevel}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Logo Maker Tab */}
        <TabsContent value="logo" className="space-y-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-gray-700 bg-gray-800/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl text-white">
                  <ImageIcon className="w-5 h-5 text-purple-400" />
                  Logo Upload & Settings
                </CardTitle>
                <CardDescription className="text-gray-400">Add and customize a logo for your QR code</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <Button
                      onClick={() => logoInputRef.current?.click()}
                      variant="outline"
                      className="flex-1 h-12 border-gray-600 text-white hover:bg-gray-700"
                      data-testid="upload-logo-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {logoImage ? 'Change Logo' : 'Upload Logo'}
                    </Button>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    
                    {logoImage && (
                      <Button
                        onClick={() => setLogoImage(null)}
                        variant="destructive"
                        className="h-12"
                        data-testid="remove-logo-btn"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>
                  
                  {logoImage && (
                    <div className="flex items-center justify-center p-4 bg-gray-900/50 rounded-lg border border-gray-700">
                      <img 
                        src={logoImage.src} 
                        alt="Logo preview" 
                        className="max-w-[120px] max-h-[120px] object-contain"
                      />
                    </div>
                  )}
                </div>

                {logoImage && (
                  <>
                    <div className="space-y-4">
                      <div className="space-y-3">
                        <Label className="text-base font-medium text-white">Logo Size</Label>
                        <div className="pt-2">
                          <Slider
                            value={logoSize}
                            onValueChange={setLogoSize}
                            min={10}
                            max={30}
                            step={1}
                            className="w-full"
                          />
                          <div className="flex justify-between text-sm text-gray-400 mt-2">
                            <span>Small</span>
                            <span className="font-medium text-white">{logoSize[0]}%</span>
                            <span>Large</span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-base font-medium text-white">Logo Shape</Label>
                        <div className="grid grid-cols-3 gap-2">
                          <button
                            onClick={() => setLogoShape('square')}
                            className={`p-4 rounded-lg border-2 transition-all ${logoShape === 'square' ? 'border-cyan-400 bg-cyan-400/10' : 'border-gray-600 hover:border-cyan-400/50'}`}
                          >
                            <div className="w-12 h-12 bg-cyan-400/20 border-2 border-cyan-400/50 mx-auto"></div>
                            <p className="text-xs mt-2 text-white">Square</p>
                          </button>
                          <button
                            onClick={() => setLogoShape('rounded')}
                            className={`p-4 rounded-lg border-2 transition-all ${logoShape === 'rounded' ? 'border-cyan-400 bg-cyan-400/10' : 'border-gray-600 hover:border-cyan-400/50'}`}
                          >
                            <div className="w-12 h-12 bg-cyan-400/20 border-2 border-cyan-400/50 rounded-lg mx-auto"></div>
                            <p className="text-xs mt-2 text-white">Rounded</p>
                          </button>
                          <button
                            onClick={() => setLogoShape('circle')}
                            className={`p-4 rounded-lg border-2 transition-all ${logoShape === 'circle' ? 'border-cyan-400 bg-cyan-400/10' : 'border-gray-600 hover:border-cyan-400/50'}`}
                          >
                            <div className="w-12 h-12 bg-cyan-400/20 border-2 border-cyan-400/50 rounded-full mx-auto"></div>
                            <p className="text-xs mt-2 text-white">Circle</p>
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 rounded-lg border border-gray-700">
                          <div className="space-y-0.5">
                            <Label htmlFor="logo-border" className="text-base font-medium text-white">White Border</Label>
                            <p className="text-sm text-gray-400">Adds a border around the logo</p>
                          </div>
                          <Switch
                            id="logo-border"
                            checked={logoBorder}
                            onCheckedChange={setLogoBorder}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <Alert className="bg-blue-900/30 border-blue-600/50">
                      <Info className="w-4 h-4 text-blue-400" />
                      <AlertDescription className="text-blue-200">
                        For best results, use a simple logo with transparent background. 
                        Higher error correction levels work better with larger logos.
                      </AlertDescription>
                    </Alert>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="border-gray-700 bg-gray-800/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl text-white">
                  <Type className="w-5 h-5 text-pink-400" />
                  Text Logo Creator
                </CardTitle>
                <CardDescription className="text-gray-400">Create a custom logo with text</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <Label className="text-base font-medium text-white">Text</Label>
                      <Input
                        value={textLogoText}
                        onChange={(e) => setTextLogoText(e.target.value)}
                        placeholder="QR"
                        className="h-11 bg-gray-900 border-gray-600 text-white"
                        maxLength={4}
                        data-testid="text-logo-input"
                      />
                      <p className="text-xs text-gray-400">Max 4 characters recommended</p>
                    </div>
                    
                    <div className="space-y-3">
                      <Label className="text-base font-medium text-white">Font</Label>
                      <Select value={textLogoFont} onValueChange={setTextLogoFont}>
                        <SelectTrigger className="h-11 bg-gray-900 border-gray-600 text-white">
                          <SelectValue placeholder="Select font" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-600">
                          {fontOptions.map((font) => (
                            <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                              {font}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-base font-medium text-white">Text Size</Label>
                    <div className="pt-2">
                      <Slider
                        value={textLogoFontSize}
                        onValueChange={setTextLogoFontSize}
                        min={24}
                        max={72}
                        step={4}
                        className="w-full"
                      />
                      <div className="flex justify-between text-sm text-gray-400 mt-2">
                        <span>Small</span>
                        <span className="font-medium text-white">{textLogoFontSize[0]}px</span>
                        <span>Large</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <Label className="text-base font-medium text-white">Text Color</Label>
                      <div className="flex gap-3 items-center">
                        <div 
                          className="w-10 h-10 rounded-lg border-2 border-gray-600 overflow-hidden cursor-pointer"
                          style={{ backgroundColor: textLogoColor }}
                        >
                          <input
                            type="color"
                            value={textLogoColor}
                            onChange={(e) => setTextLogoColor(e.target.value)}
                            className="w-full h-full cursor-pointer opacity-0"
                          />
                        </div>
                        <Input
                          type="text"
                          value={textLogoColor}
                          onChange={(e) => setTextLogoColor(e.target.value)}
                          className="flex-1 h-10 bg-gray-900 border-gray-600 text-white"
                          placeholder="#FFFFFF"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label className="text-base font-medium text-white">Background Color</Label>
                      <div className="flex gap-3 items-center">
                        <div 
                          className="w-10 h-10 rounded-lg border-2 border-gray-600 overflow-hidden cursor-pointer"
                          style={{ backgroundColor: textLogoBgColor }}
                        >
                          <input
                            type="color"
                            value={textLogoBgColor}
                            onChange={(e) => setTextLogoBgColor(e.target.value)}
                            className="w-full h-full cursor-pointer opacity-0"
                          />
                        </div>
                        <Input
                          type="text"
                          value={textLogoBgColor}
                          onChange={(e) => setTextLogoBgColor(e.target.value)}
                          className="flex-1 h-10 bg-gray-900 border-gray-600 text-white"
                          placeholder="#04D9FF"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Logo Shape for Text Logo */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium text-white">Logo Shape</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => setLogoShape('square')}
                        className={`p-3 rounded-lg border-2 transition-all ${logoShape === 'square' ? 'border-cyan-400 bg-cyan-400/10' : 'border-gray-600 hover:border-cyan-400/50'}`}
                      >
                        <div className="w-8 h-8 bg-cyan-400/20 border-2 border-cyan-400/50 mx-auto"></div>
                        <p className="text-xs mt-1 text-white">Square</p>
                      </button>
                      <button
                        onClick={() => setLogoShape('rounded')}
                        className={`p-3 rounded-lg border-2 transition-all ${logoShape === 'rounded' ? 'border-cyan-400 bg-cyan-400/10' : 'border-gray-600 hover:border-cyan-400/50'}`}
                      >
                        <div className="w-8 h-8 bg-cyan-400/20 border-2 border-cyan-400/50 rounded-lg mx-auto"></div>
                        <p className="text-xs mt-1 text-white">Rounded</p>
                      </button>
                      <button
                        onClick={() => setLogoShape('circle')}
                        className={`p-3 rounded-lg border-2 transition-all ${logoShape === 'circle' ? 'border-cyan-400 bg-cyan-400/10' : 'border-gray-600 hover:border-cyan-400/50'}`}
                      >
                        <div className="w-8 h-8 bg-cyan-400/20 border-2 border-cyan-400/50 rounded-full mx-auto"></div>
                        <p className="text-xs mt-1 text-white">Circle</p>
                      </button>
                    </div>
                  </div>
                </div>

                {/* FIXED: Text Logo Preview using image URL */}
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-6 rounded-xl border border-gray-700 flex flex-col items-center justify-center space-y-4">
                  <div className="relative">
                    {/* Hidden canvas for generation */}
                    <canvas
                      ref={textLogoCanvasRef}
                      className="hidden"
                      width={200}
                      height={200}
                    />
                    {/* Visible preview using generated image URL */}
                    {textLogoPreviewUrl ? (
                      <img 
                        src={textLogoPreviewUrl} 
                        alt="Text Logo Preview" 
                        className="w-48 h-48 rounded-lg shadow-lg"
                        data-testid="text-logo-preview"
                      />
                    ) : (
                      <div className="w-48 h-48 rounded-lg shadow-lg bg-gray-700 flex items-center justify-center">
                        <Type className="w-12 h-12 text-gray-500" />
                      </div>
                    )}
                    <div className="text-center mt-4">
                      <p className="text-sm text-gray-400">
                        Live preview of your text logo
                      </p>
                    </div>
                  </div>
                  
                  <Button
                    onClick={useTextLogoInQR}
                    data-testid="apply-text-logo-btn"
                    className="w-full h-12 bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white font-bold"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Use This Logo in QR Code
                  </Button>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-white">
                    <Sparkles className="w-4 h-4 text-pink-400" />
                    Text Logo Tips
                  </h4>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• 1-2 letters work best for clarity</li>
                    <li>• Use contrasting colors for readability</li>
                    <li>• Try bold, simple fonts for best results</li>
                    <li>• Consider your QR code colors when choosing logo colors</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Scan Tab */}
        <TabsContent value="scan" className="space-y-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-gray-700 bg-gray-800/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl text-white">
                  <Camera className="w-5 h-5 text-cyan-400" />
                  QR Code Scanner
                </CardTitle>
                <CardDescription className="text-gray-400">Scan QR codes using camera or upload image</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-black/50 rounded-xl overflow-hidden aspect-video flex items-center justify-center border border-gray-700">
                  {scanning ? (
                    <video
                      ref={videoRef}
                      className="w-full h-full object-cover"
                      autoPlay
                      playsInline
                      muted
                    />
                  ) : (
                    <div className="text-center space-y-4 p-8">
                      <div className="relative inline-block">
                        <div className="w-20 h-20 border-4 border-cyan-400/30 rounded-2xl animate-pulse" />
                        <Camera className="w-10 h-10 text-cyan-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                      </div>
                      <p className="text-gray-400">Camera is ready to scan</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  {!scanning ? (
                    <Button 
                      onClick={startCameraScanning} 
                      data-testid="start-camera-btn"
                      className="flex-1 h-12 bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Start Camera Scanner
                    </Button>
                  ) : (
                    <Button 
                      onClick={stopScanning} 
                      variant="destructive" 
                      className="flex-1 h-12"
                      data-testid="stop-camera-btn"
                    >
                      Stop Scanning
                    </Button>
                  )}
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-700" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-gray-800 px-3 text-gray-400 font-medium">Or</span>
                  </div>
                </div>

                <Button
                  onClick={() => scanFileInputRef.current?.click()}
                  variant="outline"
                  className="w-full h-12 border-gray-600 text-white hover:bg-gray-700"
                  data-testid="upload-qr-scan-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload QR Code Image
                </Button>
                <input
                  ref={scanFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleScanFile}
                  className="hidden"
                />
                
                <Alert className="bg-blue-900/30 border-blue-600/50">
                  <Info className="w-4 h-4 text-blue-400" />
                  <AlertDescription className="text-blue-200">
                    <strong>Note:</strong> Scanning works best with high-contrast QR codes. For colored QR codes, try the "Classic" preset when generating.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            <Card className="border-gray-700 bg-gray-800/50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl text-white">
                  <QrCode className="w-5 h-5" />
                  Scan Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {scanResult.includes('Password Protected') && (
                  <div className="space-y-4 bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                    <Label htmlFor="scan-password" className="text-base font-medium text-white">
                      Enter Password to Decrypt
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="scan-password"
                        type="password"
                        placeholder="Enter the password"
                        value={scanPassword}
                        onChange={(e) => setScanPassword(e.target.value)}
                        className="flex-1 h-11 bg-gray-900 border-gray-600 text-white"
                        data-testid="scan-password-input"
                      />
                      <Button 
                        onClick={decryptWithPassword}
                        className="h-11 bg-cyan-600 hover:bg-cyan-500"
                        data-testid="decrypt-btn"
                      >
                        Decrypt
                      </Button>
                    </div>
                  </div>
                )}

                <div className="bg-gradient-to-b from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700 min-h-[300px]">
                  <Label className="text-base font-medium mb-3 block text-white">Decoded Content</Label>
                  {scanResult ? (
                    <div className="space-y-4">
                      <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                        <pre className="whitespace-pre-wrap break-all text-sm font-mono text-white" data-testid="scan-result">
                          {scanResult}
                        </pre>
                      </div>
                      
                      <div className="flex gap-3">
                        <Button
                          onClick={() => {
                            navigator.clipboard.writeText(scanResult);
                            toast.success('Copied to clipboard!');
                          }}
                          variant="outline"
                          className="flex-1 h-11 border-gray-600 text-white hover:bg-gray-700"
                          data-testid="copy-result-btn"
                        >
                          Copy to Clipboard
                        </Button>
                        
                        {scanResult.startsWith('http') && (
                          <Button
                            onClick={() => window.open(scanResult, '_blank')}
                            className="flex-1 h-11 bg-cyan-600 hover:bg-cyan-500"
                            data-testid="open-link-btn"
                          >
                            Open Link
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-12">
                      <div className="w-16 h-16 border-2 border-dashed border-gray-600 rounded-2xl flex items-center justify-center">
                        <QrCode className="w-8 h-8 text-gray-500" />
                      </div>
                      <div>
                        <p className="text-gray-400 font-medium">No QR code scanned yet</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Use the camera or upload an image to scan a QR code
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Help Tab */}
        <TabsContent value="help">
          <Card className="border-gray-700 bg-gray-800/50 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl text-white">
                <Shield className="w-6 h-6 text-cyan-400" />
                How QR Studio Works
              </CardTitle>
              <CardDescription className="text-base text-gray-400">
                Everything you need to know about creating and scanning secure QR codes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-cyan-900/20 border border-cyan-700/50">
                    <h3 className="text-lg font-semibold mb-2 text-cyan-400 flex items-center gap-2">
                      🔐 Password Protection
                    </h3>
                    <p className="text-gray-400">
                      Your data is encrypted using <strong className="text-white">XOR encryption</strong> before being encoded. 
                      The password is never stored - it's required to decrypt and read the content.
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-purple-900/20 border border-purple-700/50">
                    <h3 className="text-lg font-semibold mb-2 text-purple-400">📱 Scanning</h3>
                    <p className="text-gray-400">
                      Scan using your device camera or upload images. Password-protected QR codes 
                      require the correct password to decrypt the content.
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-pink-900/20 border border-pink-700/50">
                    <h3 className="text-lg font-semibold mb-2 text-pink-400">🎨 Customization</h3>
                    <ul className="text-gray-400 space-y-2">
                      <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0 text-pink-400" />
                        <span>Choose from preset themes or create custom colors</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ImageIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-pink-400" />
                        <span>Add your logo in circle, square, or rounded shape</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Type className="w-4 h-4 mt-0.5 flex-shrink-0 text-pink-400" />
                        <span>Create text logos with custom colors and fonts</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Palette className="w-4 h-4 mt-0.5 flex-shrink-0 text-pink-400" />
                        <span>Add frames with custom text</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-gray-900 border border-gray-700">
                    <h3 className="text-lg font-semibold mb-2 text-white">🔒 Privacy Guarantee</h3>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>• All processing happens locally in your browser</li>
                      <li>• Your data never leaves your device</li>
                      <li>• No cookies, tracking, or storage</li>
                      <li>• Generated QR codes are completely offline</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Alert className="bg-gradient-to-r from-cyan-900/30 to-purple-900/30 border-cyan-700/50">
                <Info className="w-5 h-5 text-cyan-400" />
                <AlertDescription className="text-gray-300">
                  <strong>Pro Tip:</strong> For logos covering up to 30% of the QR code, use High error 
                  correction (H level). This ensures maximum scannability while maintaining your brand presence.
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="p-4 rounded-lg bg-gray-900/50 border border-gray-700">
                  <div className="text-2xl font-bold text-cyan-400">100%</div>
                  <div className="text-sm text-gray-400">Local Processing</div>
                </div>
                <div className="p-4 rounded-lg bg-gray-900/50 border border-gray-700">
                  <div className="text-2xl font-bold text-purple-400">XOR</div>
                  <div className="text-sm text-gray-400">Encryption</div>
                </div>
                <div className="p-4 rounded-lg bg-gray-900/50 border border-gray-700">
                  <div className="text-2xl font-bold text-pink-400">30%</div>
                  <div className="text-sm text-gray-400">Max Logo Coverage</div>
                </div>
                <div className="p-4 rounded-lg bg-gray-900/50 border border-gray-700">
                  <div className="text-2xl font-bold text-green-400">Text</div>
                  <div className="text-sm text-gray-400">Logo Creator</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QRCodeStudio;